export function getQueryParamsAsObject() {
    const queryParams = new URLSearchParams(window.location.search);
    const paramsObj = {};

    // Iterate over all keys and values
    for (const [key, value] of queryParams) {
        // Store them in an object
        paramsObj[key] = value;
    }

    return paramsObj;
}
export function getQueryParamsAsString() {
    return new URL(window.location.href).search; // This includes the leading '?'
}

export function removeURLParameters(params) {
    const urlParams = new URLSearchParams(window.location.search);
    params.forEach(param => {
        if (urlParams.has(param)) {
            urlParams.delete(param);
        }
    });
    if (urlParams.toString() === '') {
        window.history.replaceState({}, '', `${location.pathname}`);
    }else{
        window.history.replaceState({}, '', `${location.pathname}?${urlParams}`);
    }
}

/**
 * Escapes HTML special characters to prevent XSS attacks
 * Converts text to HTML-safe representation that displays correctly when used with innerHTML
 * @param {string} text - The text to escape
 * @returns {string} The escaped text safe for HTML insertion
 */
export function escapeHtml(text) {
    if (!text) return '';
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
}

/**
 * Validates redirect URLs to prevent XSS attacks via javascript:, data:, and other dangerous protocols
 * Allows any valid http/https URL or relative path
 * @param {string} url - The URL to validate
 * @returns {boolean} True if the URL is safe to use in window.location.href, false otherwise
 */
export function isValidRedirectUrl(url) {
    if (!url) return false;

    // Normalize and trim the URL
    const normalizedUrl = url.trim().toLowerCase();

    // Block dangerous protocols that can execute JavaScript
    const dangerousProtocols = [
        'javascript:',
        'data:',
        'vbscript:',
        'file:',
        'blob:',
        'about:'
    ];

    for (const protocol of dangerousProtocols) {
        if (normalizedUrl.startsWith(protocol)) {
            console.warn(`[Security] Blocked dangerous redirect URL protocol: ${protocol}`);
            return false;
        }
    }

    // Try to parse as URL to ensure it's well-formed
    try {
        // If it starts with / or ./ or ../ it's a relative URL - these are safe
        if (normalizedUrl.startsWith('/') || normalizedUrl.startsWith('./') || normalizedUrl.startsWith('../')) {
            return true;
        }

        // Try to parse as absolute URL
        const parsed = new URL(url);

        // Only allow http and https protocols for absolute URLs
        if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') {
            console.warn(`[Security] Blocked non-HTTP redirect URL protocol: ${parsed.protocol}`);
            return false;
        }

        return true;
    } catch (e) {
        // If it doesn't start with a protocol and isn't a relative path, it might be a malformed URL
        // Allow it if it looks like a relative path (doesn't contain :)
        if (!normalizedUrl.includes(':')) {
            return true;
        }

        console.warn('[Security] Blocked malformed redirect URL:', url);
        return false;
    }
}

/**
 * Validates redirect URLs for SSO and OAuth flows.
 * Enforces an explicit domain allowlist — only learner.plus and cmefy.com properties
 * (plus localhost in development) are permitted as redirect targets.
 * This prevents open-redirect token exfiltration: the Firebase ID token appended
 * to the redirect URL must never be delivered to an untrusted host.
 * @param {string} url - The URL to validate
 * @returns {boolean} True if the URL is safe to use as an SSO/OAuth redirect, false otherwise
 */
export function isValidSSORedirectUrl(url) {
    if (!url) return false;

    const normalizedUrl = url.trim().toLowerCase();

    // Browsers silently strip \t, \n, \r from anywhere in a URL and leading/trailing C0 controls
    // before resolving it. Without this, 'java\tscript:alert(1)' slips past startsWith() below
    // but executes when assigned to window.location.href.
    // Legitimate redirect URIs never contain these characters, so stripping is lossless for valid input.
    // Note: percent-encoded variants like %09 or %0A are unaffected — they are printable ASCII, not control bytes.
    const strippedUrl = normalizedUrl
        .replace(/[\t\n\r]/g, '')
        .replace(/^[\u0000-\u001F]+|[\u0000-\u001F]+$/g, '');

    // Block dangerous protocols unconditionally
    const dangerousProtocols = ['javascript:', 'data:', 'vbscript:', 'file:', 'blob:', 'about:'];
    for (const protocol of dangerousProtocols) {
        if (strippedUrl.startsWith(protocol)) {
            console.warn(`[Security] Blocked dangerous SSO redirect protocol: ${protocol}`);
            return false;
        }
    }

    // Relative paths are same-origin — safe only if not protocol-relative (// or /\)
    if (normalizedUrl.startsWith('./') || normalizedUrl.startsWith('../')) {
        return true;
    }
    if (normalizedUrl.startsWith('/') && !normalizedUrl.startsWith('//') && !normalizedUrl.startsWith('/\\')) {
        return true;
    }

    // Absolute URLs must be http/https and on an approved domain
    try {
        const parsed = new URL(url);

        if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') {
            console.warn(`[Security] Blocked non-HTTP SSO redirect protocol: ${parsed.protocol}`);
            return false;
        }

        const hostname = parsed.hostname.toLowerCase();

        // Approved SSO/OAuth redirect domains.
        // Add new partner domains here explicitly — do not use wildcards.
        const allowedExact = [
            'learner.plus',
            'www.learner.plus',
            'staging.learner.plus',
            'beta.learner.plus',
            'cmefy.com',
            'www.cmefy.com',
            'localhost',  // local development
        ];

        // Allow any subdomain of cmefy.com (mirrors existing CORS config: *.cmefy.com)
        const isAllowed = allowedExact.includes(hostname) || hostname.endsWith('.cmefy.com');

        if (!isAllowed) {
            console.warn(`[Security] Blocked SSO redirect to unlisted domain: ${hostname}`);
            return false;
        }

        return true;
    } catch (e) {
        console.warn('[Security] Blocked malformed SSO redirect URL:', url);
        return false;
    }
}

/**
 * Validates redirect URLs for transcript pages - only allows same-domain (learner.plus) redirects
 * More restrictive than isValidRedirectUrl() to prevent phishing via transcript pages
 * @param {string} url - The URL to validate
 * @returns {boolean} - True if URL is valid and same-domain, false otherwise
 */
export function isValidTranscriptRedirectUrl(url) {
    if (!url) return false;

    // Normalize and trim the URL
    const normalizedUrl = url.trim().toLowerCase();

    // Block dangerous protocols that can execute JavaScript
    const dangerousProtocols = [
        'javascript:',
        'data:',
        'vbscript:',
        'file:',
        'blob:',
        'about:'
    ];

    for (const protocol of dangerousProtocols) {
        if (normalizedUrl.startsWith(protocol)) {
            console.warn(`[Security] Blocked dangerous transcript redirect URL protocol: ${protocol}`);
            return false;
        }
    }

    // Try to parse as URL to ensure it's well-formed
    try {
        // If it starts with / or ./ or ../ it's a relative URL - these are safe (same domain)
        if (normalizedUrl.startsWith('/') || normalizedUrl.startsWith('./') || normalizedUrl.startsWith('../')) {
            return true;
        }

        // Try to parse as absolute URL
        const parsed = new URL(url);

        // Only allow http and https protocols for absolute URLs
        if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') {
            console.warn(`[Security] Blocked non-HTTP transcript redirect URL protocol: ${parsed.protocol}`);
            return false;
        }

        // Only allow same-domain redirects (learner.plus domains)
        const allowedDomains = ['learner.plus', 'www.learner.plus'];
        if (!allowedDomains.includes(parsed.hostname)) {
            console.warn(`[Security] Blocked external transcript redirect to domain: ${parsed.hostname}`);
            return false;
        }

        return true;
    } catch (e) {
        // If it doesn't start with a protocol and isn't a relative path, it might be a malformed URL
        // Allow it if it looks like a relative path (doesn't contain :)
        if (!normalizedUrl.includes(':')) {
            return true;
        }

        console.warn('[Security] Blocked malformed transcript redirect URL:', url);
        return false;
    }
}